import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useCustomRouter from '@hooks/useCustomRouter';
import FAQs from '../../FAQs';
import TopNavbar from '../../TopNavbar';
import StockModelList from '../../StockModelsList';
import FilterBox from '../../FilterBox';
import SortBox from '@components/SortBox';
import TrimSelectorDuck from '../../../redux/trimSelector/trimSelector.duck';
import FilterDuck from '../../../redux/filters/filter.duck';
import useTranslations from '@hooks/useTranslations';
import { buildParameterizedPathname, extractParamsFromPathname } from '@utils/url.utils';
import routes from '../../../constants/routes';
import { StockModelPageProps } from './';
import { Redux } from 'src/redux/redux.interface';
import Main from '@components/Layout/Main';
import { setPageTitleDescriptionProperties } from '@utils/appConfig.utils';
import {
    IS_B2B,
    STOCK_TRIMS_PER_PAGE,
    ModalVersion,
    FILTER_CATEGORY,
    NEED_HAND_SETTING_KEY,
} from '../../../constants/main';
import Paginator from '../../Paginator';
import StockService from '../../../services/stock/stock.service';
import { filtersToUrl, stockCarFiltersExportToParam } from '../../../services/filters/utils/Filters.utils';
import LoaderOverlay from '../../LoaderOverlay';
import { useGTM } from '@hooks/useGTM';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import CarTitle from '../../CarTitle';
import CustomError from '../../CustomError';
import { ERROR_CODE } from '../../../pages/_error';
import { stringify } from 'query-string';
import { getModelTitle } from '../../../services/carList/utils/Car.utils';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import useOnClickOutside from '@hooks/useOnClickOutside';
import SidebarFilterBox from '@components/SidebarFilterBox';
import PriceLegalText from '@components/PriceLegalText';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import ContentDuck from '../../../redux/content/content.duck';
import { useUserDuck } from '@hooks/useUserDuck';
import DealerDuck from 'src/redux/dealer/dealer.duck';
import UserDuck from 'src/redux/user/user.duck';
import { useGlobalDuck } from '@hooks/useGlobalDuck';
import { EUserLoginType } from 'src/interfaces/User';

export const StockModelPageTemplate: FC<StockModelPageProps> = ({ className }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();
    const { pushProductImpressions, pushTrimPageLoad } = useGTM();
    const filters = useSelector((state) => FilterDuck.getFilters(state));
    const router = useCustomRouter();
    const dealerId = (router.query?.idsitegeo as string) ?? '';
    const dealer = useSelector((state) => DealerDuck.getDealer(state));
    const userAuth = useSelector((state) => UserDuck.getOwnState(state)).userAuth;
    const { nameplateBodyStyleSlug, modelId, bodyStyleId } = extractParamsFromPathname(router.asPath);
    const { carJourney } = useGlobalDuck();
    const allTrims = useSelector((state: Redux) => TrimSelectorDuck.getOwnState(state).stock.stockTrims);
    const paymentJourney = useSelector((state: Redux) => FilterDuck.getPaymentJourneyType(state));
    const sort = useSelector((state: Redux) => FilterDuck.getSorting(state));
    const filterCategories = useSelector((state: Redux) => FilterDuck.getFilterCategories(state));
    const geoLocation = useSelector((state: Redux) => FilterDuck.getGeoLocation(state));
    const geoLocationName = useSelector((state: Redux) => FilterDuck.getGeoLocationName(state));
    const distanceRadius = useSelector((state: Redux) => FilterDuck.getDistanceRadius(state));
    const { isUserLoggedInAsDealer } = useUserDuck();
    const filteredStockTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredStockTrims(state));
    const filteredOutStockTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredOutStockTrims(state));
    const totalFilteredItems = useSelector(
        (state: Redux) => TrimSelectorDuck.getOwnState(state).stock.totalFilteredItems
    );
    const resultsPerPage = useSelector((state: Redux) => TrimSelectorDuck.getOwnState(state).stock.itemsPerPage);
    const areAvailableDemoVehiclesForTrimPage = useSelector(
        (state: Redux) => TrimSelectorDuck.getOwnState(state).stock.areAvailableDemoTrims
    );
    const currentPageNumber = useSelector(
        (state: Redux) => TrimSelectorDuck.getOwnState(state).stock.currentPageNumber
    );
    const sampleFilteredTrim = filteredStockTrims[0];

    const settings = useSelector((state: Redux) => ContentDuck.getSettings(state));

    const isLocalizationEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_FORCE_LOCALIZATION_ENABLED);

    const [isLoading, setIsLoading] = useState(false);
    const [showBanner, setShowBanner] = useState<boolean>(totalFilteredItems <= resultsPerPage);

    const filterRef = useRef();

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedCategoryName, setSelectedCategoryName] = useState(null);

    const needHandBlockOrder = Number(settings.find((setting) => setting.key === NEED_HAND_SETTING_KEY)?.value || 0);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [allTrims]);

    useEffect(() => {
        pushTrimPageLoad(allTrims, true);
    }, []);

    useEffect(() => {
        setShowBanner(
            Math.ceil(totalFilteredItems / resultsPerPage) === 0 ||
                Math.ceil(totalFilteredItems / resultsPerPage) === currentPageNumber
        );
        filteredStockTrims.length && pushProductImpressions('trim', filteredStockTrims);
    }, [filteredStockTrims]);

    useEffect(() => {
        if (isLocalizationEnabled && !geoLocation && !router?.query?.idsitegeo) {
            dispatch(
                UIDuck.openModal({
                    data: { data: {} },
                    callbacks: {},
                    modalType: UIDuck.MODAL_TYPES.POSTAL_CODE,
                    modalVersion: ModalVersion.v2,
                    modalProperties: {
                        centerModeOnDesktop: true,
                    },
                })
            );
        }
    }, [geoLocation]);

    const handleBackButtonClick = () => {
        const q = typeof window !== 'undefined' ? location.search : stringify(router.query);
        router.push(
            buildParameterizedPathname(
                `${routes.ROOT}${filtersToUrl(
                    filterCategories,
                    filters,
                    sort,
                    q,
                    geoLocation,
                    distanceRadius,
                    geoLocationName,
                    null,
                    true
                )}`,
                carJourney
            )
        );
    };

    const fullBrand = t('title');

    const onLoadMoreButtonClick = useCallback(
        async (selectedPage: { selected: number }) => {
            const nextPageIndex = selectedPage.selected + 1;

            setShowBanner(Math.ceil(totalFilteredItems / resultsPerPage) === nextPageIndex);

            const filterTitle = document.getElementById('filter-title');
            const y = filterTitle.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });

            setIsLoading(true);

            try {
                const allVehicles = await StockService.getStockVehicles(
                    modelId,
                    bodyStyleId,
                    nextPageIndex,
                    needHandBlockOrder > 0 ? resultsPerPage - 1 : resultsPerPage,
                    {}
                );
                const isAgentDealer = userAuth.loginType === EUserLoginType.DEALER && userAuth?.detail?.dealerCode;
                const filteredVehicles = await StockService.getStockVehicles(
                    modelId,
                    bodyStyleId,
                    nextPageIndex,
                    needHandBlockOrder > 0 ? resultsPerPage - 1 : resultsPerPage,
                    {
                        ...stockCarFiltersExportToParam(filters),
                        ...(dealerId && dealer?.attachedNVActorCode && { rrdi7: dealer?.attachedNVActorCode }),
                        ...(!dealerId && isAgentDealer && { rrdi7: userAuth?.detail?.dealerCode }),
                    },
                    geoLocation,
                    distanceRadius
                );

                await dispatch(
                    TrimSelectorDuck.setStockTrims({
                        stockTrims: allVehicles.data.vehicles,
                        totalItems: allVehicles.data.totalVehiclesCount,
                        pageNumber: nextPageIndex,
                        filteredStockTrims: filteredVehicles.data.vehicles,
                        totalFilteredItems: filteredVehicles.data.totalVehiclesCount,
                        areAvailableDemoTrims: areAvailableDemoVehiclesForTrimPage,
                    })
                );
            } catch (error: any) {
                dispatch(TrimSelectorDuck.setStockDataLoadError(JSON.stringify(error)));
            }

            setIsLoading(false);
        },
        [currentPageNumber, nameplateBodyStyleSlug, paymentJourney, geoLocation, distanceRadius, filters]
    );

    const pageTitleDescriptionProperties = setPageTitleDescriptionProperties(fullBrand, sampleFilteredTrim);

    const pageCanonicalLink = buildParameterizedPathname(routes.SELECTOR, carJourney, {
        nameplateBodyStyleSlug,
        modelId,
        bodyStyleId,
    });

    const openFilterByCategory = (category: FILTER_CATEGORY) => {
        setSelectedCategoryName(category);
        setIsFilterOpen(true);
    };

    useOnClickOutside(filterRef, () => setIsFilterOpen(false), []);

    return (
        <>
            <div ref={filterRef}>
                <SidebarFilterBox
                    selectedCategoryName={selectedCategoryName}
                    isOpen={isFilterOpen}
                    closeFilter={() => setIsFilterOpen(false)}
                />
            </div>
            <Main
                title={
                    IS_B2B
                        ? t('seo.trimPage.b2b.title', pageTitleDescriptionProperties)
                        : t('seo.trimPage.title', pageTitleDescriptionProperties)
                }
                description={
                    IS_B2B
                        ? t('seo.trimPage.b2b.description', pageTitleDescriptionProperties)
                        : t('seo.trimPage.description', pageTitleDescriptionProperties)
                }
                canonicalLink={pageCanonicalLink}
                hasOpenFilter={isFilterOpen}
            >
                <div className={className}>
                    {isLoading && <LoaderOverlay loading={isLoading} />}

                    <TopNavbar title={t('trim.stock.model.title')} onGoBack={handleBackButtonClick} />

                    <FilterBox
                        isUserLoggedInAsDealer={isUserLoggedInAsDealer}
                        openFilter={(category: FILTER_CATEGORY) => openFilterByCategory(category)}
                    />

                    {!!allTrims.length ? (
                        <>
                            <div className="filter-container">
                                <SortBox isSortEnabled={true} countOfVehicles={totalFilteredItems} filters={filters} />
                            </div>
                            <StockModelList
                                showLastItemBanner={showBanner}
                                vehicleList={filteredStockTrims}
                                vehicleOutList={filteredOutStockTrims}
                            />
                        </>
                    ) : (
                        <CustomError
                            className="stock"
                            errorType={String(ERROR_CODE.NOT_FOUND)}
                            errorTitle={t('errorPage.title.stock')}
                            returnLink={t('errorPage.returnLink.link.stock')}
                            returnLinkLabel={t('errorPage.returnLink.label.stock')}
                            titleDescription={t('errorPage.description.stock')}
                        />
                    )}

                    {totalFilteredItems > STOCK_TRIMS_PER_PAGE && (
                        <div className="showMore">
                            <Paginator
                                page={currentPageNumber}
                                className="showMore--pagination"
                                handlePageClick={onLoadMoreButtonClick}
                                total={totalFilteredItems}
                                perPage={resultsPerPage}
                            />
                        </div>
                    )}

                    <PriceLegalText className="isTrimPage" isStockJourney>
                        <p className="legalText">
                            <span
                                dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('filters.stock.legal.text')}
                            ></span>
                        </p>
                    </PriceLegalText>

                    <FAQs />
                </div>
            </Main>
        </>
    );
};

export default StockModelPageTemplate;
